import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { formatDZD } from './currency';
import { numberToFrenchWords } from './numberToWords';

// Function to generate client code based on client data
const generateClientCode = (clientData) => {
  if (!clientData) return 'N/A';
  
  // If client has a clientNumber, use it to generate CLI-XX format
  if (clientData.clientNumber) {
    const number = String(clientData.clientNumber).padStart(2, '0');
    return `CLI-${number}`;
  }
  
  // Fallback: use client ID if no clientNumber
  if (clientData.id) {
    const number = String(clientData.id).padStart(2, '0');
    return `CLI-${number}`;
  }
  
  return 'CLI-00';
};

// Function to generate item codes based on category
const generateItemCode = (item, index) => {
  // If item already has a code, use it
  if (item.itemCode || item.reference || item.sku) {
    return item.itemCode || item.reference || item.sku;
  }
  
  // Default categories with prefixes (fallback)
  const defaultCategories = [
    { name: 'Services Informatiques', prefix: 'SRV' },
    { name: 'Matériel Informatiques', prefix: 'MAT' },
    { name: 'Formation', prefix: 'FOR' },
    { name: 'Consulting', prefix: 'CST' },
    { name: 'Maintenance', prefix: 'MNT' }
  ];
  
  // Try to find matching category prefix
  let prefix = 'GEN'; // Default generic prefix
  
  if (item.category) {
    const matchedCategory = defaultCategories.find(cat => 
      cat.name.toLowerCase() === item.category.toLowerCase() ||
      item.category.toLowerCase().includes(cat.name.toLowerCase())
    );
    
    if (matchedCategory) {
      prefix = matchedCategory.prefix;
    }
  }
  
  // If no category match, use basic logic
  if (prefix === 'GEN') {
    const isService = item.type === 'service' || 
                     (item.category && item.category.toLowerCase().includes('service')) ||
                     (item.name && item.name.toLowerCase().includes('service'));
    prefix = isService ? 'SRV' : 'MAT';
  }
  
  const number = String(index + 1).padStart(2, '0');
  return `${prefix}-${number}`;
};

// Function to format bank account
const formatBankAccount = (account) => {
  if (!account || account.trim() === '') return '';
  const parts = account.trim().split(' ');
  if (parts.length < 2) return account.trim();
  
  // Find bank name index
  const bankIndex = parts.findIndex(part => 
    ['CPA', 'BADR', 'BNA', 'BEA', 'BDL', 'CNEP', 'AGB'].includes(part)
  );
  
  if (bankIndex === -1) {
  return account.trim();
  }
  
  const accountNumber = parts.slice(0, bankIndex).join(' ');
  const bankName = parts[bankIndex];
  const location = parts.slice(bankIndex + 1).join(' ');
  
  return `${accountNumber} ${bankName}${location ? ' ' + location : ''}`;
};

// Calculate dynamic column widths based on content
const calculateColumnWidths = (items, pageWidth) => {
  const availableWidth = pageWidth - 40; // margins
  
  // Calculate max item code length
  const maxItemCodeLength = items.length > 0 ? 
    Math.max(...items.map((item, index) => generateItemCode(item, index).length)) : 0;
  
  // Check for wide numbers
  const hasWideNumbers = items.some(item => {
    const quantity = parseInt(item.quantity) || 1;
    return quantity >= 10; // 2+ digits
  });
  
  // Base column widths (percentages)
  let widths = {
    no: 5,      // N°
    item: 10,   // Item
    desc: 30,   // Désignation
    unit: 5,    // U
    qty: 5,     // Q
    price: 18,  // P.U HT
    amount: 18, // Montant
    tva: 5      // %
  };
  
  // Adjust based on item code length
  if (maxItemCodeLength >= 9) { // very long codes
    widths.item = 14;
    widths.desc = 26;
  } else if (maxItemCodeLength >= 7) { // long codes
    widths.item = 12;
    widths.desc = 28;
  }
  
  // Convert percentages to actual widths
  const totalPercent = Object.values(widths).reduce((a, b) => a + b, 0);
  const actualWidths = {};
  Object.keys(widths).forEach(key => {
    actualWidths[key] = (widths[key] / totalPercent) * availableWidth;
  });
  
  return actualWidths;
};

// Fonction pour mapper les modes de paiement vers leur libellé complet
const getPaymentMethodLabel = (method) => {
  const methodMap = {
    'virement': 'Virement bancaire',
    'cheque': 'Chèque',
    'especes': 'Espèces',
    'carte': 'Carte bancaire',
    'effet': 'Effet de commerce',
    'traite': 'Traite',
    'compensation': 'Compensation',
    'prélèvement': 'Prélèvement automatique',
    'mixte': 'Plusieurs modes',
    'crédit_documentaire': 'Crédit documentaire',
    'crédit_compte': 'Crédit sur compte client',
    'acompte': 'Acompte + solde',
    'echeances': 'Paiement échelonné',
    'livraison': 'À la livraison'
  };
  return methodMap[method] || method;
};

// High-quality print-to-PDF generation (same quality as "Imprimer")
export const generateDocumentPDF = async (documentType, formData, clientData, settings) => {
  try {
    // Generate filename first
    const getDocumentPrefix = (docType) => {
      const prefixMap = {
        'invoice': settings?.documentPrefixes?.invoice || 'FAC',
        'proforma': settings?.documentPrefixes?.proforma || 'PRO',
        'quote-request': settings?.documentPrefixes?.quoteRequest || 'DDV',
        'quote': settings?.documentPrefixes?.quote || 'DEV',
        'purchase-order': settings?.documentPrefixes?.purchaseOrder || 'BC',
        'delivery-note': settings?.documentPrefixes?.deliveryNote || 'BL',
        'shipping-slip': settings?.documentPrefixes?.shippingSlip || 'BE',
        'reception-note': settings?.documentPrefixes?.receptionNote || 'BR',
        'return-slip': settings?.documentPrefixes?.returnSlip || 'BRT',
        'stock-sheet': settings?.documentPrefixes?.stockSheet || 'FST',
        'material-exit': settings?.documentPrefixes?.materialExit || 'BSM',
        'attachment': settings?.documentPrefixes?.attachment || 'ATT'
      };
      return prefixMap[docType] || 'DOC';
    };
    
    const prefix = getDocumentPrefix(documentType);
    const formatDateForFile = (dateString) => {
      if (!dateString) return 'SANS-DATE';
      return new Date(dateString).toLocaleDateString('fr-FR').replace(/\//g, '-');
    };
    
    const dateStr = formatDateForFile(formData.date);
    const fileName = `${prefix}-${formData.number || 'XXXX'}-${dateStr}.pdf`;

    // Check if browser supports the modern print-to-PDF API
    if (window.showSaveFilePicker && navigator.userAgent.includes('Chrome')) {
      return await generateWithModernAPI(fileName);
    } else {
      // Fallback to window.print() with enhanced styling
      return await generateWithPrintDialog(fileName);
    }
    
  } catch (error) {
    console.error('Erreur lors de la génération PDF:', error);
    
    // Final fallback to high-quality screenshot
    return await generateWithScreenshot(documentType, formData, clientData, settings);
  }
};

// Modern print-to-PDF using Chrome's native API (highest quality)
const generateWithModernAPI = async (fileName) => {
  try {
    // Hide action buttons during print
    const actionButtons = document.querySelector('.preview-actions');
    const originalDisplay = actionButtons ? actionButtons.style.display : null;
    if (actionButtons) actionButtons.style.display = 'none';

    // Apply print styles temporarily
    const printStyles = document.createElement('style');
    printStyles.textContent = `
      @page {
        size: A4;
        margin: 0 !important;
      }
      
      @media print {
        body * { visibility: hidden; }
        .document-container, .document-container * { visibility: visible; }
        
        body {
          margin: 0 !important;
          padding: 0 !important;
          background: white !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .document-container { 
          position: absolute; 
          left: 0; 
          top: 0; 
          width: 100%; 
          background: white !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          margin: 0 !important;
          padding: 0 !important;
        }
        
        .document-paper {
          box-shadow: none !important;
          border-radius: 0 !important;
          margin: 0 auto !important;
          background: white !important;
          width: 210mm !important;
          height: 297mm !important;
          min-height: 297mm !important;
          max-height: 297mm !important;
          padding: 15mm !important;
          page-break-inside: avoid;
          overflow: hidden !important;
          box-sizing: border-box !important;
          font-family: Arial, sans-serif !important;
          font-size: 14px !important;
          line-height: 1.4 !important;
          transform: none !important;
          scale: 1 !important;
        }
        
        .preview-actions,
        .signature-overlay,
        .signature-canvas-overlay {
          display: none !important;
        }
        
        /* Force blue border color for recipient box */
        .recipient-box {
          border: 1px solid #3b82f6 !important;
          border-color: #3b82f6 !important;
        }

        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `;
    document.head.appendChild(printStyles);

    // Use window.print() which triggers the save dialog
    window.print();

    // Clean up
    setTimeout(() => {
      document.head.removeChild(printStyles);
      if (actionButtons && originalDisplay !== null) {
        actionButtons.style.display = originalDisplay;
      } else if (actionButtons) {
        actionButtons.style.display = '';
      }
    }, 1000);

    return true;

  } catch (error) {
    console.error('Erreur avec l\'API moderne:', error);
    throw error;
  }
};

// Enhanced print dialog approach (very high quality)
const generateWithPrintDialog = async (fileName) => {
  try {
    // Create a new window with specific settings to minimize headers/footers
    const printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=no,resizable=no,toolbar=no,directories=no,location=no,menubar=no,status=no');
    if (!printWindow) {
      throw new Error('Popup blocked - please allow popups for this site');
    }

    // Get the document content
    const documentElement = document.querySelector('.document-paper');
    if (!documentElement) {
      throw new Error('Document preview element not found');
    }

    // Get all CSS stylesheets
    const styles = Array.from(document.styleSheets)
      .map(styleSheet => {
        try {
          return Array.from(styleSheet.cssRules)
            .map(rule => rule.cssText)
            .join('\n');
        } catch (e) {
          return '';
        }
      })
      .join('\n');

    // Create clean HTML for printing
    const printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${fileName}</title>
        <style>
          ${styles}
          
          @page {
            size: A4;
            margin: 0 !important;
          }
          
          html {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            width: 100% !important;
            height: 100% !important;
          }
          
          /* Force override browser print margins */
          * {
            box-sizing: border-box !important;
          }
          
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            font-family: Arial, sans-serif;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            width: 100% !important;
            height: 100% !important;
          }
          
          .document-paper {
            width: 210mm !important;
            height: 297mm !important;
            min-height: 297mm !important;
            max-height: 297mm !important;
            background: white !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            margin: 0 auto !important;
            padding: 15mm !important;
            page-break-inside: avoid;
            overflow: hidden !important;
            box-sizing: border-box !important;
            font-family: Arial, sans-serif !important;
            font-size: 14px !important;
            line-height: 1.4 !important;
            transform: none !important;
            scale: 1 !important;
          }
          
          @media print {
            body { 
              margin: 0 !important; 
              padding: 0 !important; 
              background: white !important;
            }
            .document-paper { 
              margin: 0 auto !important; 
              box-shadow: none !important; 
              border-radius: 0 !important;
              width: 210mm !important;
              height: 297mm !important;
              min-height: 297mm !important;
              max-height: 297mm !important;
              padding: 15mm !important;
              overflow: hidden !important;
              box-sizing: border-box !important;
              font-family: Arial, sans-serif !important;
              transform: none !important;
              scale: 1 !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="document-paper">
          ${documentElement.innerHTML.replace(/Mode de paiement: ([a-zA-Z]+)/g, (match, method) => `Mode de paiement: ${getPaymentMethodLabel(method)}`)}
        </div>
      </body>
      </html>`;

    // Write content to new window
    printWindow.document.write(printHTML);
    printWindow.document.close();

    return true;

  } catch (error) {
    console.error('Erreur avec la fenêtre d\'impression:', error);
    throw error;
  }
};

// Enhanced screenshot fallback with maximum quality
export const generateWithScreenshot = async (documentType, formData, clientData, settings) => {
  try {
    const documentElement = document.querySelector('.document-paper');
    if (!documentElement) {
      throw new Error('Document preview element not found');
    }

    // Hide UI elements
    const actionButtons = document.querySelector('.preview-actions');
    const signatureOverlay = document.querySelector('.signature-overlay');
    const signatureCanvas = document.querySelector('.signature-canvas-overlay');
    
    const originalActionDisplay = actionButtons ? actionButtons.style.display : null;
    const originalSignatureDisplay = signatureOverlay ? signatureOverlay.style.display : null;
    const originalCanvasDisplay = signatureCanvas ? signatureCanvas.style.display : null;
    
    if (actionButtons) actionButtons.style.display = 'none';
    if (signatureOverlay) signatureOverlay.style.display = 'none';
    if (signatureCanvas) signatureCanvas.style.display = 'none';

    // Save original styles
    const originalTransform = documentElement.style.transform;
    const originalScale = documentElement.style.scale;
    const originalHeight = documentElement.style.height;
    const originalMinHeight = documentElement.style.minHeight;
    const originalMaxHeight = documentElement.style.maxHeight;
    const originalPadding = documentElement.style.padding;
    const originalOverflow = documentElement.style.overflow;
    
    // Ensure the document is sized exactly like the preview
    documentElement.style.transform = 'none';
    documentElement.style.scale = '1';
    documentElement.style.width = '210mm';
    documentElement.style.height = 'auto';
    documentElement.style.minHeight = '297mm';
    documentElement.style.maxHeight = 'none';
    documentElement.style.overflow = 'visible';
    
    // Keep the original padding to match the preview exactly
    // documentElement.style.padding = '15mm';

    // Wait for fonts to load
    await document.fonts.ready;

    // Ensure the footer is properly displayed
    const footer = documentElement.querySelector('.simple-footer');
    if (footer) {
      footer.style.position = 'relative';
      footer.style.bottom = 'auto';
    }

    // Capture with maximum quality settings
    const canvas = await html2canvas(documentElement, {
      scale: 2.5, // Higher resolution for better quality
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      removeContainer: false,
      logging: false,
      imageTimeout: 0,
      onclone: (clonedDoc) => {
        // Enhance quality in cloned document
        const clonedElement = clonedDoc.querySelector('.document-paper');
        if (clonedElement) {
          clonedElement.style.transform = 'none';
          clonedElement.style.scale = '1';
          clonedElement.style.width = '210mm';
          clonedElement.style.height = 'auto';
          clonedElement.style.minHeight = '297mm';
          clonedElement.style.maxHeight = 'none';
          clonedElement.style.overflow = 'visible';
          // Keep original padding to match preview
          // clonedElement.style.padding = '15mm';
          clonedElement.style.boxSizing = 'border-box';
          clonedElement.style.background = '#ffffff';
          clonedElement.style.fontSmoothing = 'antialiased';
          clonedElement.style.webkitFontSmoothing = 'antialiased';
          
          // Ensure footer is visible
          const clonedFooter = clonedElement.querySelector('.simple-footer');
          if (clonedFooter) {
            clonedFooter.style.position = 'relative';
            clonedFooter.style.bottom = 'auto';
            clonedFooter.style.visibility = 'visible';
            clonedFooter.style.display = 'block';
          }
        }
      }
    });

    // Restore UI elements
    if (actionButtons && originalActionDisplay !== null) {
      actionButtons.style.display = originalActionDisplay;
    } else if (actionButtons) {
      actionButtons.style.display = '';
    }
    
    if (signatureOverlay && originalSignatureDisplay !== null) {
      signatureOverlay.style.display = originalSignatureDisplay;
    } else if (signatureOverlay) {
      signatureOverlay.style.display = '';
    }
    
    if (signatureCanvas && originalCanvasDisplay !== null) {
      signatureCanvas.style.display = originalCanvasDisplay;
    } else if (signatureCanvas) {
      signatureCanvas.style.display = '';
    }
    
    // Restore original styles
    documentElement.style.transform = originalTransform;
    documentElement.style.scale = originalScale;
    documentElement.style.height = originalHeight;
    documentElement.style.minHeight = originalMinHeight;
    documentElement.style.maxHeight = originalMaxHeight;
    documentElement.style.padding = originalPadding;
    documentElement.style.overflow = originalOverflow;

    // Create PDF with exact A4 dimensions
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });
    
    // A4 dimensions
    const pdfWidth = 210;
    const pdfHeight = 297;
    
    // Get the image data with high quality
    const imgData = canvas.toDataURL('image/png', 1.0);
    
    // Calculate aspect ratio of the captured content
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    
    // Calculate scaling to fit the entire content properly
    // We need to ensure the whole content fits within the A4 page
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    
    // Calculate dimensions that maintain aspect ratio
    const finalWidth = imgWidth * ratio;
    const finalHeight = imgHeight * ratio;
    
    // Calculate centering position
    const xOffset = (pdfWidth - finalWidth) / 2;
    const yOffset = 0; // Align to top
    
    // Add image to PDF with proper scaling to fit the entire content
    pdf.addImage(imgData, 'PNG', xOffset, yOffset, finalWidth, finalHeight, '', 'FAST');

    // Generate filename
    const getDocumentPrefix = (docType) => {
      const prefixMap = {
        'invoice': settings?.documentPrefixes?.invoice || 'FAC',
        'proforma': settings?.documentPrefixes?.proforma || 'PRO',
        'quote-request': settings?.documentPrefixes?.quoteRequest || 'DDV',
        'quote': settings?.documentPrefixes?.quote || 'DEV',
        'purchase-order': settings?.documentPrefixes?.purchaseOrder || 'BC',
        'delivery-note': settings?.documentPrefixes?.deliveryNote || 'BL',
        'shipping-slip': settings?.documentPrefixes?.shippingSlip || 'BE',
        'reception-note': settings?.documentPrefixes?.receptionNote || 'BR',
        'return-slip': settings?.documentPrefixes?.returnSlip || 'BRT',
        'stock-sheet': settings?.documentPrefixes?.stockSheet || 'FST',
        'material-exit': settings?.documentPrefixes?.materialExit || 'BSM',
        'attachment': settings?.documentPrefixes?.attachment || 'ATT'
      };
      return prefixMap[docType] || 'DOC';
    };
    
    const prefix = getDocumentPrefix(documentType);
    const formatDateForFile = (dateString) => {
      if (!dateString) return 'SANS-DATE';
      return new Date(dateString).toLocaleDateString('fr-FR').replace(/\//g, '-');
    };
    
    const dateStr = formatDateForFile(formData.date);
    const fileName = `${prefix}-${formData.number || 'XXXX'}-${dateStr}.pdf`;
    
    pdf.save(fileName);
    
    return true;
    
  } catch (error) {
    console.error('Erreur lors de la génération PDF par capture:', error);
    throw error;
  }
};

// Download function for direct file download
export const downloadDocumentPDF = async (documentType, formData, clientData, settings) => {
  try {
    // Force direct PDF download using screenshot method for consistent results
    return await generateWithScreenshot(documentType, formData, clientData, settings);
    
  } catch (error) {
    console.error('Erreur lors du téléchargement PDF:', error);
    throw error;
  }
};

// Preview function
export const previewDocumentPDF = async (documentType, formData, clientData, settings) => {
  try {
    const documentElement = document.querySelector('.document-paper');
    if (!documentElement) {
      throw new Error('Document preview element not found');
    }

    const canvas = await html2canvas(documentElement, {
      scale: 1,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });

    return canvas.toDataURL('image/png');
    
  } catch (error) {
    console.error('Erreur lors de la prévisualisation PDF:', error);
    throw error;
  }
}; 